const MAX_ARG_SIZE_BYTES = 8 * 1024; //8KB is a reasonable limit for console arguments to avoid Android bridge issues

const ORIGINAL_CONSOLE = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

let installed = false;

function isPassthroughPrimitive(arg: unknown): boolean {
  if (arg === null || arg === undefined) return true;
  const t = typeof arg;
  return (
    t === 'number' ||
    t === 'boolean' ||
    t === 'bigint' ||
    t === 'symbol' ||
    t === 'function'
  );
}

function truncateString(value: string, maxBytes: number): string {
  if (value.length * 2 <= maxBytes) return value;
  const keep = Math.floor(maxBytes / 4);
  return `${value.slice(0, keep)}…[TRUNCATED ${value.length - keep} chars]`;
}

function describeError(err: Error, maxBytes: number): object {
  return {
    name: err.name,
    message: truncateString(err.message, maxBytes),
    stack:
      typeof err.stack === 'string'
        ? truncateString(err.stack, maxBytes)
        : undefined,
  };
}

function describeBuffer(value: ArrayBuffer | ArrayBufferView): string {
  return `[Buffer: ${value.byteLength} bytes]`;
}

function describeObject(value: object, maxBytes: number): unknown {
  try {
    const json = JSON.stringify(value);
    if (json === undefined) return '[Unserializable Object]';
    if (json.length * 2 <= maxBytes) return value;
    const keys = Object.keys(value).slice(0, 8).join(',');
    return `[Object: ${json.length} chars - truncated. keys=${keys}]`;
  } catch {
    const keys = Object.keys(value).slice(0, 8).join(',');
    return `[Unserializable Object: keys=${keys}]`;
  }
}

export function truncateArg(
  arg: unknown,
  maxBytes = MAX_ARG_SIZE_BYTES,
): unknown {
  if (isPassthroughPrimitive(arg)) return arg;
  if (typeof arg === 'string') return truncateString(arg, maxBytes);
  if (arg instanceof Error) return describeError(arg, maxBytes);
  if (arg instanceof Blob) return `[Blob: ${arg.size} bytes]`;
  if (arg instanceof ArrayBuffer || ArrayBuffer.isView(arg)) {
    return describeBuffer(arg);
  }
  if (typeof arg === 'object' && arg !== null) {
    return describeObject(arg, maxBytes);
  }
  return arg;
}

// MC20-18329: Wraps console.* to clamp argument size before the Capacitor
// Android bridge formats them. Must run before any boot-time logging fires —
// keep this as the FIRST executable statement in main.tsx. Do not reorder.
export function installConsoleGuard(): void {
  if (installed) return;
  installed = true;
  const levels: readonly (keyof typeof ORIGINAL_CONSOLE)[] = [
    'log',
    'info',
    'debug',
    'warn',
    'error',
  ];
  for (const level of levels) {
    console[level] = (...args: unknown[]) => {
      ORIGINAL_CONSOLE[level](...args.map((a) => truncateArg(a)));
    };
  }
}
